// ============================================================
// Centralized Dayboard invalidation — cross-device sync for any
// mutation that affects what the Dayboard shows.
//
// PURPOSE:
// The Dayboard's primary update mechanism is Supabase Realtime
// (`postgres_changes` on the tickets table + its joins). When
// realtime is healthy, every device sees every mutation within
// ~1 second. But realtime is not 100% reliable: brief CLOSED
// windows during auth recovery, postgres replication lag, RLS
// edge cases on joined tables, and channel-name day scoping all
// produce gaps where Device B doesn't receive an event Device A
// produced.
//
// The fix is a shop-scoped Supabase Broadcast channel that any
// mutation flow can ping. Broadcasts ride the same realtime
// websocket but don't depend on postgres replication or RLS —
// they're explicit RPC-style messages delivered to every
// subscriber of the same topic. Receivers dispatch a local
// `ticket-updated` event, which the existing
// `refetchTicketsTick` listener in Dashboard converts into a
// date-scoped refetch.
//
// USAGE FROM ANY MUTATION FLOW:
//
//   import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
//
//   // After a successful mutation:
//   await notifyDayboardInvalidated({
//     reason: 'ticket-reassign',
//     shopId,                  // required
//     ticketId,                // optional, helpful for debugging
//     scheduledDate,           // optional
//     metadata: { ... },       // optional
//   });
//
// The helper does BOTH:
//   1. Dispatches the local `ticket-updated` event immediately
//      (so the SENDER device's date-scoped refetch fires without
//      a roundtrip through the broadcast).
//   2. Sends the broadcast (with `self: false`) so OTHER devices
//      receive a `dayboard-invalidate` event and dispatch their
//      own local `ticket-updated`.
//
// RECEIVER DEBOUNCE:
// Multiple invalidations within 200ms collapse into a single
// refetch on the receiving device — avoids refetch storms on
// cascade fan-outs (e.g., reassign-ticket touches N downstream
// rows, but a single broadcast is enough).
//
// CHANNEL LIFECYCLE:
// Channels are cached per shopId in a module-level Map. The
// Dashboard mounts a setup useEffect that calls
// `ensureDayboardBroadcastChannel(shopId)` once; subsequent
// senders reuse the same channel handle. When the Dashboard
// unmounts (route change, sign-out), it calls
// `disposeDayboardBroadcastChannel(shopId)` to remove the
// channel cleanly.
//
// FALLBACK SAFETY:
// If the channel isn't subscribed yet (race during initial
// mount) or `send()` throws, the helper logs a non-fatal warn
// and returns. The local `ticket-updated` dispatch has already
// fired by that point, so the SENDER device's UI converges even
// if the broadcast was a no-op for receivers.
// ============================================================

import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { logRealtimeStatus } from '@/utils/realtimeStatusLog';

export const DAYBOARD_INVALIDATE_EVENT = 'dayboard-invalidate';
export const TICKET_UPDATED_EVENT = 'ticket-updated';

export interface DayboardInvalidationPayload {
  /** Free-text marker that identifies the originating mutation.
   *  Used for console logging on receivers; not interpreted. */
  reason:
    | 'drag-drop-reassign'
    | 'kebab-reassign'
    | 'kebab-date-change'
    | 'kebab-delete'
    | 'kebab-parking-lot'
    | 'ticket-create'
    | 'ticket-edit'
    | 'job-add'
    | 'job-edit'
    | 'job-delete'
    | 'vehicle-edit'
    | 'customer-edit'
    | 'autobump'
    | 'cascade'
    | 'eod-convert'
    | 'eod-rollover-save'
    | 'rollover-completion'
    | 'timer-update'
    | 'optimizer-apply'
    | 'optimizer-reassign'
    | 'eod-monitor'
    | 'realtime-self-heal'
    | 'tab-visible'
    | string;
  /** Required. Shop the Dayboard belongs to. Channel is scoped by shopId. */
  shopId: string;
  ticketId?: string;
  scheduledDate?: string;
  metadata?: Record<string, unknown>;
}

const channels = new Map<string, RealtimeChannel>();

/** Receiver-side debounce so cascade fan-outs collapse to one refetch. */
let receiverDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const RECEIVER_DEBOUNCE_MS = 200;

function dispatchLocalTicketUpdated(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(TICKET_UPDATED_EVENT));
  } catch {
    // Non-DOM environment (SSR, tests); ignore.
  }
}

function debouncedDispatchLocalTicketUpdated(): void {
  if (receiverDebounceTimer) clearTimeout(receiverDebounceTimer);
  receiverDebounceTimer = setTimeout(() => {
    dispatchLocalTicketUpdated();
    receiverDebounceTimer = null;
  }, RECEIVER_DEBOUNCE_MS);
}

/**
 * Lazily create + subscribe the shop-scoped broadcast channel.
 * Idempotent: subsequent calls with the same shopId return the
 * cached channel handle. Mount this once at Dashboard mount time;
 * mutation flows look up the same channel via this getter when
 * they call `notifyDayboardInvalidated`.
 */
export function ensureDayboardBroadcastChannel(shopId: string): RealtimeChannel {
  const existing = channels.get(shopId);
  if (existing) return existing;

  const channel = supabase.channel(`dayboard-broadcast-${shopId}`, {
    config: { broadcast: { self: false, ack: false } },
  });

  channel.on('broadcast', { event: DAYBOARD_INVALIDATE_EVENT }, (msg: any) => {
    const reason = msg?.payload?.reason ?? 'unknown';
    console.log('[DayboardInvalidation] received broadcast:', reason);
    debouncedDispatchLocalTicketUpdated();
  });

  channel.subscribe((status, err) => {
    // Batch 11: normalized log. Intentional CLOSED on
    // disposeDayboardBroadcastChannel → debug. Real failures still
    // warn/error.
    logRealtimeStatus('[DayboardInvalidation]', status, err);
  });

  channels.set(shopId, channel);
  return channel;
}

/** How many mounted surfaces are listening on each shop's channel. */
const holders = new Map<string, number>();

/**
 * Listen on the shop's EXISTING dayboard broadcast channel for as long as
 * the caller is mounted; returns the release. Reference-counted, so the
 * Dashboard and the FOH queue badge share the one subscription and neither
 * unmounting tears it down under the other. Not a new channel.
 */
export function retainDayboardBroadcastChannel(shopId: string): () => void {
  ensureDayboardBroadcastChannel(shopId);
  holders.set(shopId, (holders.get(shopId) ?? 0) + 1);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const left = (holders.get(shopId) ?? 1) - 1;
    if (left > 0) {
      holders.set(shopId, left);
      return;
    }
    holders.delete(shopId);
    disposeDayboardBroadcastChannel(shopId);
  };
}

/**
 * Tear down the cached channel for a shop. Called by Dashboard
 * on unmount or shop change. Subsequent
 * `ensureDayboardBroadcastChannel` calls will re-create.
 */
export function disposeDayboardBroadcastChannel(shopId: string): void {
  const channel = channels.get(shopId);
  if (!channel) return;
  try {
    supabase.removeChannel(channel);
  } catch (err) {
    console.warn('[DayboardInvalidation] removeChannel failed (non-fatal):', err);
  }
  channels.delete(shopId);
}

/**
 * Notify all Dayboards in the same shop that something changed
 * and they should refetch their current selected-day data.
 *
 * Fires:
 *   1. Local `ticket-updated` event (immediate, sender-side refetch).
 *   2. Supabase Broadcast `dayboard-invalidate` (other devices).
 *
 * Safe to call from any mutation flow. Async (returns a Promise)
 * but callers don't need to await it — failures log a non-fatal
 * warn and don't surface to the operator.
 */
export async function notifyDayboardInvalidated(
  payload: DayboardInvalidationPayload,
): Promise<void> {
  // Sender-side refetch — fires immediately, no debounce, so the
  // device that just performed the mutation sees its own change.
  dispatchLocalTicketUpdated();

  if (!payload.shopId) {
    console.warn('[DayboardInvalidation] missing shopId — local-only refetch fired, broadcast skipped');
    return;
  }

  try {
    const channel = ensureDayboardBroadcastChannel(payload.shopId);
    await channel.send({
      type: 'broadcast',
      event: DAYBOARD_INVALIDATE_EVENT,
      payload,
    });
  } catch (err) {
    console.warn('[DayboardInvalidation] broadcast send failed (non-fatal):', err);
  }
}
